/**
 * reports-api.ts — zestawienia sprzedaży: parametry w adresie, pobieranie danych, CSV.
 *
 * Parametry raportu żyją w ADRESIE zakładki, nie w stanie komponentu — dzięki temu raport da się
 * odświeżyć, wrócić do niego i mieć obok kartoteki. Kodek jest tutaj, żeby ekran nie sklejał
 * adresów ręcznie, a bramka nieznanych wartości działała w jednym miejscu.
 */

import {
  HM_REPORT_DETAILS_CHUNK,
  HM_SALES_REPORT_DIMENSIONS,
  type HmReportMoney,
  type HmSalesReportDetail,
  type HmSalesReportDimension,
  type HmSalesReportResponse,
  type HmSalesReportRow,
} from '@saas/shared';

import type { RangePreset } from '@/components/handel-magazyn/HmListShell';
import { api } from '@/lib/api';

export interface SalesReportParams {
  preset: RangePreset;
  dateFrom: string;
  dateTo: string;
  buyerId: number | null;
  typeCode: string | null;
  departmentId: number | null;
  series: string | null;
  status: 'buffer' | 'issued' | null;
  dim: HmSalesReportDimension;
  /** `true` = użytkownik chce widzieć pozycje dokumentów, nie tylko nagłówki. */
  positions: boolean;
  /** `true` = dociągaj szczegóły dokumentów (rejestr pieniężny, pozycje). */
  details: boolean;
  /**
   * Zawężenie do WSKAZANYCH dokumentów — raport „wykonaj dla zaznaczonych". Pusta lista = cały
   * zakres. Identyfikatory idą w adresie, więc raport da się odświeżyć i przekazać dalej.
   */
  ids: number[];
}

/**
 * Zestawienia sprzedaży. Wszystkie liczą się z tego samego zbioru dokumentów i tą samą funkcją —
 * różnią się WYMIAREM podsumowania i ziarnem wiersza. Dlatego to jeden ekran z czterema wejściami,
 * a nie cztery ekrany: różnica między „wg towarów" a „wg kontrahentów" to jeden parametr, a nie
 * inna logika. Każdy ma własny adres, żeby mógł stać w osobnej zakładce.
 */
export const SALES_REPORTS = {
  'sprzedaz-za-okres': { dim: 'none', positions: false, details: false },
  'sprzedaz-wg-kontrahentow': { dim: 'buyer', positions: false, details: false },
  'sprzedaz-wg-towarow': { dim: 'product', positions: true, details: true },
  'rejestr-vat-sprzedazy': { dim: 'vatRate', positions: true, details: true },
} as const satisfies Record<
  string,
  { dim: HmSalesReportDimension; positions: boolean; details: boolean }
>;

export type SalesReportSlug = keyof typeof SALES_REPORTS;

export const SALES_REPORT_SLUGS = Object.keys(SALES_REPORTS) as SalesReportSlug[];

export function isSalesReportSlug(value: string | undefined): value is SalesReportSlug {
  return !!value && value in SALES_REPORTS;
}

export function salesReportPath(slug: SalesReportSlug): string {
  return `/handel-magazyn/sprzedaz/zestawienia/${slug}`;
}

/**
 * Klucz i18n nazwy raportu — ten sam w menu, w tytule ekranu i na zakładce.
 * Mapa jest WYPISANA wprost, nie sklejana ze sluga: klucz budowany dynamicznie jest martwym
 * polem skanera tłumaczeń i brak wpisu przeszedłby przez bramkę niezauważony.
 */
const TITLE_KEYS: Record<SalesReportSlug, string> = {
  'sprzedaz-za-okres': 'tenant.handelMagazyn.reports.salesByPeriod.title',
  'sprzedaz-wg-kontrahentow': 'tenant.handelMagazyn.reports.salesByContractor.title',
  'sprzedaz-wg-towarow': 'tenant.handelMagazyn.reports.salesByProduct.title',
  'rejestr-vat-sprzedazy': 'tenant.handelMagazyn.reports.vatRegister.title',
};

export function salesReportTitleKey(slug: SalesReportSlug): string {
  return TITLE_KEYS[slug];
}

/** Parametry startowe raportu: jego własne nastawy na wierzchu wartości domyślnych. */
export function reportDefaults(slug: SalesReportSlug, base: SalesReportParams): SalesReportParams {
  return { ...base, ...SALES_REPORTS[slug] };
}

export function toSearchParams(p: SalesReportParams): URLSearchParams {
  const sp = new URLSearchParams();
  sp.set('preset', p.preset);
  sp.set('dateFrom', p.dateFrom);
  sp.set('dateTo', p.dateTo);
  if (p.buyerId != null) sp.set('buyerId', String(p.buyerId));
  if (p.typeCode) sp.set('typeCode', p.typeCode);
  if (p.departmentId != null) sp.set('departmentId', String(p.departmentId));
  if (p.series) sp.set('series', p.series);
  if (p.status) sp.set('status', p.status);
  sp.set('dim', p.dim);
  if (p.positions) sp.set('grain', 'position');
  if (p.details) sp.set('details', 'true');
  if (p.ids.length > 0) sp.set('ids', p.ids.join(','));
  return sp;
}

function readInt(sp: URLSearchParams, name: string): number | null {
  const raw = sp.get(name);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isInteger(n) ? n : null;
}

/**
 * Adres → parametry. Nieznane wartości (podrobiony wymiar, śmieci w identyfikatorze) cicho
 * wracają do wartości domyślnej — adres bywa sklejany ręcznie i nie może wywracać ekranu.
 */
export function parseSearchParams(sp: URLSearchParams): SalesReportParams {
  const dimRaw = sp.get('dim') ?? 'none';
  const dim = (HM_SALES_REPORT_DIMENSIONS as readonly string[]).includes(dimRaw)
    ? (dimRaw as HmSalesReportDimension)
    : 'none';
  const statusRaw = sp.get('status');
  return {
    preset: (sp.get('preset') as RangePreset) || 'custom',
    dateFrom: sp.get('dateFrom') ?? '',
    dateTo: sp.get('dateTo') ?? '',
    buyerId: readInt(sp, 'buyerId'),
    typeCode: sp.get('typeCode') || null,
    departmentId: readInt(sp, 'departmentId'),
    series: sp.get('series') || null,
    status: statusRaw === 'buffer' || statusRaw === 'issued' ? statusRaw : null,
    dim,
    positions: sp.get('grain') === 'position',
    details: sp.get('details') === 'true',
    ids: (sp.get('ids') ?? '')
      .split(',')
      .map((raw) => Number(raw))
      .filter((n) => Number.isInteger(n) && n > 0),
  };
}

export async function fetchSalesReport(
  companyId: string,
  params: SalesReportParams,
  signal?: AbortSignal,
): Promise<HmSalesReportResponse> {
  const sp = toSearchParams(params);
  sp.set('companyId', companyId);
  sp.delete('preset'); // preset jest informacją dla ekranu, API zna tylko konkretne daty
  const res = await api.get<HmSalesReportResponse>(`/handel-magazyn/reports/sales?${sp}`, {
    signal,
  });
  return res.data;
}

export async function fetchReportDetails(
  companyId: string,
  ids: number[],
  signal?: AbortSignal,
): Promise<{ details: HmSalesReportDetail[]; failed: number[] }> {
  const res = await api.post<{ details: HmSalesReportDetail[]; failed: number[] }>(
    `/handel-magazyn/reports/sales/details?companyId=${companyId}`,
    { ids },
    { signal },
  );
  return res.data;
}

/** Dzieli identyfikatory na paczki obsługiwane przez trasę szczegółów. */
export function chunkIds(ids: number[]): number[][] {
  const chunks: number[][] = [];
  for (let i = 0; i < ids.length; i += HM_REPORT_DETAILS_CHUNK) {
    chunks.push(ids.slice(i, i + HM_REPORT_DETAILS_CHUNK));
  }
  return chunks;
}

/* ---------------------------------------------------------------------- CSV */

/**
 * CSV budujemy W PRZEGLĄDARCE, a nie osobną trasą API — cały raport jest już tutaj, więc
 * odtwarzanie go na serwerze oznaczałoby drugie przejście po całym zakresie Symfonii (przy
 * wymiarach szczegółowych: drugie N+1) wyłącznie po to, żeby wypisać te same liczby.
 *
 * Średnik jako separator i sygnatura UTF-8 — Excel w polskiej lokalizacji tylko tak otwiera
 * plik od razu w kolumnach, bez kreatora importu.
 */
function csvCell(value: string | number | null | undefined): string {
  const text = String(value ?? '');
  return /[";\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function buildCsv(rows: (string | number | null)[][]): string {
  // Sygnatura UTF-8 jako jawna sekwencja — niewidoczny znak w kodzie to proszenie się o kłopot.
  return `\ufeff${rows.map((r) => r.map(csvCell).join(';')).join('\r\n')}\r\n`;
}

export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export interface CsvBuildInput {
  /** Nagłówki kolumn — już przetłumaczone przez ekran. */
  headers: string[];
  /** Wiersze raportu w kolejności wyświetlania; koszyk poprzedza swoje wiersze. */
  sections: { label: string | null; rows: HmSalesReportRow[]; totals: HmReportMoney[] }[];
  grandTotals: HmReportMoney[];
  totalsLabel: string;
  withPositions: boolean;
}

/** Raport → wiersze CSV, z podsumami koszyków i sumą całkowitą, dokładnie jak na ekranie. */
export function salesReportToCsv(input: CsvBuildInput): string {
  const out: (string | number | null)[][] = [input.headers];
  // Wiersz podsumy: etykieta w pierwszej kolumnie, kwoty pod właściwymi nagłówkami. Kolumn
  // między nimi jest tyle, ile kolumn opisowych — z pozycjami dwie więcej (towar, ilość).
  const gap = input.withPositions ? 4 : 2;
  const moneyRow = (label: string, money: HmReportMoney[]) =>
    money.map((m) => [
      label,
      ...Array.from({ length: gap }, () => ''),
      m.currency,
      m.net,
      m.vatPln,
      m.gross,
    ]);

  for (const section of input.sections) {
    if (section.label !== null) out.push([section.label]);
    for (const row of section.rows) {
      out.push([
        row.buyerName ?? '',
        row.documentNumber,
        row.issueDate?.slice(0, 10) ?? '',
        ...(input.withPositions ? [row.productCode ?? '', row.quantity ?? ''] : []),
        row.currency,
        row.netValue,
        row.vatPln,
        row.grossValue,
      ]);
    }
    if (section.label !== null) out.push(...moneyRow(`${section.label} — razem`, section.totals));
  }
  out.push(...moneyRow(input.totalsLabel, input.grandTotals));
  return buildCsv(out);
}
